// EntriesTable.js - Aktualisiert mit CSS-Klassen statt Tailwind
import React from 'react';
import { formatDate } from '../utils/dateUtils';
import './EntriesTable.css';

const EntriesTable = ({ 
  entries = [], 
  stundenarten = [], 
  onEdit, 
  viewMode = 'card', 
  editingEntry = null,
  onEditSubmit,
  onCancelEdit,
  auftraege = [],
  onToggleView,
  onDelete
}) => {
  // Hilfsfunktionen
  const getStundenartName = (artId) => {
    if (!artId) {
      console.warn('Keine Stundenart-ID übergeben');
      return 'Keine Stundenart';
    }
    console.log('Suche Stundenart:', artId, 'in:', stundenarten);
    const art = stundenarten.find(s => s.Sart === artId);
    return art ? art.SarBezeichnung : 'Unbekannte Stundenart';
  };

  const getAuftragName = (aufId) => {
    if (!aufId) {
      console.warn('Keine Auftrags-ID übergeben');
      return 'Kein Auftrag';
    }
    console.log('Suche Auftrag:', aufId, 'in:', auftraege);
    const auftrag = auftraege.find(a => a.AufID === aufId);
    return auftrag ? `${auftrag.AufNr} - ${auftrag.AufBez}` : 'Unbekannter Auftrag';
  };

  const getAuftragTitel = (aufId) => {
    const auftrag = auftraege.find(a => a.AufID === aufId);
    return auftrag ? `${auftrag.AufNr} - ${auftrag.AufObjekt}` : 'Unbekannt';
  };

  const getStundenartBezeichnung = (sartId) => {
    const art = stundenarten.find(a => a.Sart === sartId);
    return art ? art.SarBezeichnung : 'Unbekannt';
  };

  const formatFullDate = (dateString) => {
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      // Wochentag in Deutsch hinzufügen
      const weekday = new Intl.DateTimeFormat('de-DE', { weekday: 'long' }).format(date);
      // Ersten Buchstaben groß schreiben
      const capitalizedWeekday = weekday.charAt(0).toUpperCase() + weekday.slice(1);
      return `${capitalizedWeekday}, ${day}.${month}.${year}`;
    } catch (e) {
      console.error("Fehler beim Formatieren des Datums mit Jahr:", e);
      return dateString;
    }
  };

  const formatDateShort = (dateString) => {
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const weekday = new Intl.DateTimeFormat('de-DE', { weekday: 'long' }).format(date).substring(0, 2);
      return `${weekday} ${day}.${month}`;
    } catch (e) {
      console.error("Fehler beim Formatieren des Datums:", e);
      return dateString;
    }
  };

  const getArtBezeichnung = (sarId) => {
    try {
      const numericSarId = parseInt(sarId);
      const art = stundenarten.find(art => art.Sart === numericSarId);
      return art?.SarBezeichnung || 'Stunden';
    } catch (e) {
      console.error("Fehler beim Abrufen der Art:", e);
      return 'Stunden';
    }
  };

  const getArtShort = (sarId) => {
    const fullName = getArtBezeichnung(sarId);
    return fullName.substring(0, 3);
  };

  const getArtType = (sarId) => {
    try {
      const numericSarId = parseInt(sarId);
      const art = stundenarten.find(art => art.Sart === numericSarId);
      const bezeichnung = art?.SarBezeichnung?.toLowerCase() || '';
      if (bezeichnung.includes('kilometer')) return 'kilometer';
      if (bezeichnung.includes('spesen')) return 'spesen';
      return 'stunden';
    } catch (e) {
      console.error("Fehler beim Bestimmen des Typs:", e);
      return 'stunden';
    }
  };

  const formatAnzahl = (anzahl, artType) => {
    try {
      const num = parseFloat(anzahl) || 0;
      switch (artType) {
        case 'kilometer':
          return Math.round(num).toString();
        case 'spesen':
        case 'stunden':
          return num.toFixed(2);
        default:
          return num.toFixed(2);
      }
    } catch (e) {
      console.error("Fehler beim Formatieren der Anzahl:", e);
      return '0';
    }
  };

  // Totals Berechnung
  const totals = entries.reduce((acc, entry) => {
    try {
      const artType = getArtType(entry.StuSarIDRef);
      const amount = parseFloat(entry.StuAnzahl) || 0;
      acc[artType] += amount;
    } catch (e) {
      console.error("Fehler bei der Berechnung der Summen:", e);
    }
    return acc;
  }, { stunden: 0, kilometer: 0, spesen: 0 });

  // Totals Summary Component mit Toggle-Button
  const TotalsSummary = () => (
    <div className="summary-card" style={{width: "100%", boxSizing: "border-box"}}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Summen</h2>
        
        {onToggleView && (
          <button
            onClick={onToggleView}
            className="btn btn-secondary"
          >
            {viewMode === 'card' ? 'Tabellenansicht' : 'Kartenansicht'}
          </button>
        )}
      </div>
      
      <table className="w-full">
        <tbody>
          {totals.stunden > 0 && (
            <tr className="border-b">
              <td className="py-2">Total Stunden</td>
              <td className="py-2 text-right font-bold">{totals.stunden.toFixed(2)} h</td>
            </tr>
          )}
          {totals.kilometer > 0 && (
            <tr className="border-b">
              <td className="py-2">Total Kilometer</td>
              <td className="py-2 text-right font-bold">{Math.round(totals.kilometer)} km</td>
            </tr>
          )}
          {totals.spesen > 0 && (
            <tr>
              <td className="py-2">Total Spesen</td>
              <td className="py-2 text-right font-bold">{totals.spesen.toFixed(2)} CHF</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  // Gruppiere Einträge nach Datum
  const groupedEntries = entries.reduce((groups, entry) => {
    const date = entry.StuDatum;
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(entry);
    return groups;
  }, {});

  // Sortiere die Daten
  const sortedDates = Object.keys(groupedEntries).sort();

  // Finde die Auftrags- und Stundenart-Informationen
  const getAuftragInfo = (aufId) => {
    const auftrag = auftraege.find(a => a.AufID === aufId);
    return auftrag ? `${auftrag.AufNr} - ${auftrag.AufBez}` : 'Unbekannter Auftrag';
  };

  const getStundenartInfo = (sartId) => {
    const stundenart = stundenarten.find(s => s.Sart === sartId);
    return stundenart ? stundenart.SarBezeichnung : 'Unbekannte Stundenart';
  };

  // Berechne Tagessumme (nur Stunden, keine Kilometer oder Spesen)
  const calculateDayTotal = (entries) => {
    return entries
      .filter(entry => {
        const stundenart = stundenarten.find(s => s.Sart === entry.StuSarIDRef);
        return stundenart && !stundenart.SarBezeichnung.toLowerCase().includes('kilometer') && 
               !stundenart.SarBezeichnung.toLowerCase().includes('spesen');
      })
      .reduce((sum, entry) => sum + parseFloat(entry.StuAnzahl), 0)
      .toFixed(2);
  };

  return (
    <div className="entries-container">
      <div className="view-toggle">
        <button
          onClick={onToggleView}
          className={`btn ${viewMode === 'card' ? 'btn-primary' : 'btn-secondary'}`}
        >
          {viewMode === 'card' ? 'Zur Listenansicht' : 'Zur Kartenansicht'}
        </button>
      </div>

      <div className="totals-summary">
        <div className="totals-grid">
          {totals.stunden > 0 && (
            <div className="total-item">
              <span className="total-label">Stunden</span>
              <span className="total-value">{totals.stunden.toFixed(2)}</span>
            </div>
          )}
          {totals.kilometer > 0 && (
            <div className="total-item">
              <span className="total-label">Kilometer</span>
              <span className="total-value">{Math.round(totals.kilometer)}</span>
            </div>
          )}
          {totals.spesen > 0 && (
            <div className="total-item">
              <span className="total-label">Spesen</span>
              <span className="total-value">{totals.spesen.toFixed(2)}</span>
            </div>
          )}
        </div>
      </div>

      {viewMode === 'card' ? (
        <div className="cards-view">
          {sortedDates.map(date => (
            <div key={date} className="date-group">
              <h3 className="date-header">
                <span>{formatDate(date)}</span>
                <span className="day-total">Total h: {calculateDayTotal(groupedEntries[date])}</span>
              </h3>
              <div className="cards-grid">
                {groupedEntries[date].map(entry => (
                  <div key={entry.StuPGID} className="time-card">
                    <div className="card-row">
                      <div className="card-content">
                        <div>{getAuftragInfo(entry.StuAufIDRef)}</div>
                        <div>{parseFloat(entry.StuAnzahl).toFixed(2)}</div>
                      </div>
                    </div>
                    <div className="card-row">
                      <div className="description-content">
                        <div className="description-text">{entry.StuBeschrieb || '-'}</div>
                        <div className="card-actions">
                          <button
                            onClick={() => onEdit(entry)}
                            className="btn btn-sm btn-icon"
                            title="Bearbeiten"
                          >
                            ✏️
                          </button>
                        </div>
                        <div className="description-type">{getStundenartInfo(entry.StuSarIDRef)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="list-view">
          <table className="entries-table">
            <thead>
              <tr>
                <th className="fixed-column">Datum</th>
                <th>Auftrag</th>
                <th>Beschreibung</th>
                <th className="fixed-column text-right">Stunden</th>
                <th className="text-left">Art</th>
                <th className="fixed-column">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedEntries).sort().map(([date, dayEntries]) => (
                <React.Fragment key={date}>
                  <tr className="date-row">
                    <td colSpan="3" className="text-left">{formatFullDate(date)}</td>
                    <td className="text-right" colSpan="3">Total h: {calculateDayTotal(dayEntries)}</td>
                  </tr>
                  {dayEntries.map(entry => (
                    <tr key={entry.StuPGID}>
                      <td className="fixed-column">{formatDateShort(entry.StuDatum)}</td>
                      <td>{getAuftragInfo(entry.StuAufIDRef)}</td>
                      <td className="text-left">{entry.StuBeschrieb}</td>
                      <td className="fixed-column text-right">{parseFloat(entry.StuAnzahl).toFixed(2)}</td>
                      <td className="text-left">{getStundenartInfo(entry.StuSarIDRef)}</td>
                      <td className="fixed-column">
                        <button
                          onClick={() => onEdit(entry)}
                          className="btn btn-sm btn-icon"
                          title="Bearbeiten"
                        >
                          ✏️
                        </button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EntriesTable;